import { useCallback, useMemo } from 'react';
import type { ExplodedCategoryProps } from '../ExplodedCategory/ExplodedCategory';
import { useCatalogRouting } from '../../../../useCatalogRouting';
import { useCatalogLoggingContext } from '../../../../Catalog.logging';
import { useCatalogContext } from '../../../../state/Catalog';
import type { Category } from '../../../../components/CategoryShowcase';
import { useShopStorefrontContext } from '../../../../components/ShopStorefrontProvider';
import { useProductCatalogCategoryByIdQuery } from './BottomCategoryShowcase.graphql.generated';

export const useBottomCategoryShowcaseController = (isOnScreen: boolean) => {
  const { trackCategoryNavigateAction } = useCatalogLoggingContext();
  const { goToCategory } = useCatalogRouting();
  const { selectedCategory } = useCatalogContext();

  const { config } = useShopStorefrontContext();

  const { data: selectedCategoryData, loading: isLoading } = useProductCatalogCategoryByIdQuery({
    variables: { productCatalogCategoryByIdId: selectedCategory?.id || '' },
    skip: !selectedCategory?.id || !isOnScreen,
    batchMode: 'fast',
  });
  const parentTitle = selectedCategoryData?.productCatalogCategoryById?.parent?.title;

  const bottomCategoryShowcaseList = useMemo<Category[]>(
    () =>
      selectedCategoryData?.productCatalogCategoryById?.parent?.children
        ? selectedCategoryData?.productCatalogCategoryById?.parent?.children
            .filter(item => item.id !== selectedCategoryData?.productCatalogCategoryById?.id)
            .map(({ id, publicUrlPath, showcase, isFeatured }) => {
              let imageUrl = showcase?.image?.url;
              let title = showcase?.title;
              const department = config.departmentNavSectionData.find(item => item.id === id);
              if (department) {
                imageUrl = department.imageUrl;
                title = department.title;
              }
              return {
                id,
                title: title ?? '',
                publicUrlPath: publicUrlPath,
                image: imageUrl ?? '',
                isFeatured: !!isFeatured,
              };
            })
        : ([] as Category[]),
    [config.departmentNavSectionData, selectedCategoryData?.productCatalogCategoryById?.id, selectedCategoryData?.productCatalogCategoryById?.parent?.children],
  );

  const onBrowseClick = useCallback<ExplodedCategoryProps['onBrowseCategoryClick']>(
    (category, navigationSource, skipNavigation) => {
      if (!skipNavigation) goToCategory(category.publicUrlPath);
      trackCategoryNavigateAction({ category, navigationSource, overrides: { placement: 'bottom' } });
    },
    [goToCategory, trackCategoryNavigateAction],
  );

  return {
    onBrowseClick,
    selectedCategory,
    bottomCategoryShowcaseList,
    isLoading,
    parentTitle,
  };
};
